import React, { useMemo } from 'react';

/**
 * Draws one automation as a node graph, the way you would see it on a workflow
 * canvas. Nothing here is a screenshot: the nodes and edges come from
 * src/data/automations.js, which follows the workflows and scripts in this
 * repo, so the picture cannot drift from the pipeline the way an exported PNG
 * does.
 *
 * Layout is column based. A node declares `col` and `row`; this computes the
 * rest and sizes the viewBox to fit, so the same graph scales from a phone to a
 * wide screen without a pan and zoom control nobody wants to use on a phone.
 */

const NODE_W = 168;
const NODE_H = 62;
const GAP_X = 76;
const GAP_Y = 26;
const PAD = 22;

// One shape per kind, so the graph is readable without reading every label.
const KIND = {
  trigger: { fill: '#051A24', stroke: '#051A24', text: '#F6FCFF', sub: '#9FC4D2', glyph: 'clock' },
  source: { fill: '#FFFFFF', stroke: '#C3D7E0', text: '#051A24', sub: '#51707E', glyph: 'in' },
  step: { fill: '#F6FCFF', stroke: '#C3D7E0', text: '#051A24', sub: '#51707E', glyph: 'step' },
  model: { fill: '#F6FCFF', stroke: '#34D399', text: '#051A24', sub: '#3E6A5C', glyph: 'model' },
  gate: { fill: '#FFFFFF', stroke: '#273C46', text: '#051A24', sub: '#51707E', glyph: 'gate' },
  store: { fill: '#E0EBF0', stroke: '#9FBACA', text: '#051A24', sub: '#51707E', glyph: 'store' },
  end: { fill: '#FFFFFF', stroke: '#C3D7E0', text: '#51707E', sub: '#7E97A3', glyph: 'stop' },
};

const Glyph = ({ name, color }) => {
  const p = { stroke: color, strokeWidth: 1.6, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'clock':
      return (
        <g>
          <circle cx="0" cy="0" r="7" {...p} />
          <path d="M0 -4 V0 L3 2" {...p} />
        </g>
      );
    case 'in':
      return <path d="M-7 -5 H7 M-7 0 H3 M-7 5 H7" {...p} />;
    case 'model':
      return (
        <g>
          <circle cx="0" cy="0" r="3" {...p} />
          <path d="M0 -7 V-3 M0 3 V7 M-7 0 H-3 M3 0 H7" {...p} />
        </g>
      );
    case 'gate':
      return <path d="M0 -7 L7 0 L0 7 L-7 0 Z" {...p} />;
    case 'store':
      return (
        <g>
          <path d="M-6 -5 H6 V5 H-6 Z" {...p} />
          <path d="M-6 -1 H6" {...p} />
        </g>
      );
    case 'stop':
      return <path d="M-5 -5 L5 5 M5 -5 L-5 5" {...p} />;
    default:
      return <path d="M-6 0 H6 M2 -4 L6 0 L2 4" {...p} />;
  }
};

export const WorkflowCanvas = ({ automation, className = '' }) => {
  const { nodes, edges, width, height } = useMemo(() => {
    const cols = Math.max(...automation.nodes.map((n) => n.col)) + 1;
    const rows = Math.max(...automation.nodes.map((n) => n.row)) + 1;
    const placed = automation.nodes.map((n) => ({
      ...n,
      x: PAD + n.col * (NODE_W + GAP_X),
      y: PAD + n.row * (NODE_H + GAP_Y),
    }));
    const byId = Object.fromEntries(placed.map((n) => [n.id, n]));
    return {
      nodes: placed,
      edges: automation.edges.map(([from, to, label]) => ({ from: byId[from], to: byId[to], label })),
      width: PAD * 2 + cols * NODE_W + (cols - 1) * GAP_X,
      height: PAD * 2 + rows * NODE_H + (rows - 1) * GAP_Y,
    };
  }, [automation]);

  return (
    <div className={`overflow-x-auto ${className}`} data-testid={`workflow-canvas-${automation.slug}`}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        style={{ minWidth: Math.min(width, 720), width: '100%', height: 'auto' }}
        role="img"
        aria-label={`${automation.name}: ${automation.nodes.map((n) => n.label).join(', then ')}`}
      >
        <defs>
          <pattern id={`dots-${automation.slug}`} width="18" height="18" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="#CBDDE6" />
          </pattern>
          <marker id={`head-${automation.slug}`} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
            <path d="M0 0 L7 3.5 L0 7 z" fill="#9FBACA" />
          </marker>
        </defs>

        <rect width={width} height={height} fill={`url(#dots-${automation.slug})`} rx="16" />

        {edges.map(({ from, to, label }, i) => {
          const x1 = from.x + NODE_W;
          const y1 = from.y + NODE_H / 2;
          const x2 = to.x;
          const y2 = to.y + NODE_H / 2;
          const bend = Math.max(28, (x2 - x1) / 2);
          return (
            <g key={`${from.id}-${to.id}-${i}`}>
              <path
                d={`M${x1} ${y1} C ${x1 + bend} ${y1}, ${x2 - bend} ${y2}, ${x2} ${y2}`}
                fill="none"
                stroke="#9FBACA"
                strokeWidth="1.8"
                markerEnd={`url(#head-${automation.slug})`}
              />
              {label && (
                <text
                  x={(x1 + x2) / 2}
                  y={(y1 + y2) / 2 - 6}
                  textAnchor="middle"
                  fontSize="11"
                  fill="#51707E"
                  style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                >
                  {label}
                </text>
              )}
            </g>
          );
        })}

        {nodes.map((n) => {
          const k = KIND[n.kind] || KIND.step;
          return (
            <g key={n.id} transform={`translate(${n.x} ${n.y})`}>
              <rect
                width={NODE_W}
                height={NODE_H}
                rx="12"
                fill={k.fill}
                stroke={k.stroke}
                strokeWidth={n.kind === 'gate' ? 1.8 : 1.2}
                strokeDasharray={n.kind === 'end' ? '5 4' : undefined}
              />
              <g transform="translate(24 31)">
                <Glyph name={k.glyph} color={k.text} />
              </g>
              <text x="42" y={n.note ? 27 : 36} fontSize="13" fontWeight="600" fill={k.text}>
                {n.label}
              </text>
              {n.note && (
                <text x="42" y="43" fontSize="11" fill={k.sub}>
                  {n.note}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

export default WorkflowCanvas;
